import { Checkbox, MenuItem, Select, TextField } from '@mui/material'
import { CurrencyInput, DatePicker } from '@impres-development/assets/client'
import dayjs from 'dayjs'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { ColumnFormat } from '../../lines/LinesColumns'
import { ManufacturerSearchAutocomplete, SupplierSearchAutocomplete } from 'src/helper'

// TODO: add to assets
type EditableCellProps = {
  inputType?: ColumnFormat
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any
  source?: 'manufacturer' | 'supplier'
  canEdit: boolean
  options?: { label: string; value: string | number | boolean | null }[]
  limit?: number
  alwaysEdit?: boolean
  displayValue?: ReactNode
  // TODO: refactor contractNumber to not be a prop
  contractNumber?: string | null
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange: (value: any) => void
}

function CurrencyEditableInput({
  value,
  onChange,
  onDeactivate,
}: {
  value: number | null
  onChange: (value: number | null) => void
  onDeactivate: () => void
}) {
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const input = containerRef.current?.querySelector('input')
    input?.focus()
    input?.select()
  }, [])

  return (
    <div
      className="active-cell"
      ref={containerRef}
      onBlur={e => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
          onDeactivate()
        }
      }}
    >
      <CurrencyInput fullWidth value={value} onChange={onChange} size="small" />
    </div>
  )
}

export function EditableCell({
  inputType,
  value,
  canEdit,
  source,
  options,
  limit,
  alwaysEdit = false,
  displayValue,
  contractNumber,
  onChange,
}: EditableCellProps) {
  const [isActive, setIsActive] = useState(alwaysEdit && canEdit)
  const onActivate = () => setIsActive(true)
  const onDeactivate = () => {
    if (!alwaysEdit) setIsActive(false)
  }

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (alwaysEdit) return

      if (e.key === 'Escape' || e.key === 'Enter') {
        onDeactivate()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  if (!isActive) {
    return (
      <span
        className="lines-editable-cell"
        style={{ cursor: canEdit ? 'pointer' : 'default' }}
        onClick={e => {
          e.stopPropagation()
          if (canEdit) {
            onActivate()
          }
        }}
      >
        {(displayValue !== undefined
          ? displayValue
          : inputType === 'boolean'
          ? value
            ? 'Yes'
            : 'No'
          : inputType === 'currency'
          ? value?.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
          : inputType === 'date' && value
          ? dayjs(value).utc().format('MM/DD/YYYY')
          : inputType === 'number'
          ? value?.toLocaleString('en-US')
          : inputType === 'percentage'
          ? `${(value * 100).toFixed(2)}%`
          : value) ?? '-'}
      </span>
    )
  }

  switch (inputType) {
    case 'boolean':
      return (
        <Checkbox
          checked={value}
          onChange={e => {
            onChange(e.target.checked)
          }}
          onClick={e => e.stopPropagation()}
          slotProps={{ input: { autoFocus: true, onBlur: onDeactivate } }}
        />
      )
    case 'currency':
      return (
        <CurrencyEditableInput value={value} onChange={value => onChange(value ?? 0)} onDeactivate={onDeactivate} />
      )
    case 'date': {
      const rawDate = value
      return (
        <DatePicker
          className="active-cell"
          value={rawDate ? dayjs(rawDate).utc() : null}
          size="small"
          onChange={value => {
            onChange(value ? dayjs(value).toDate() : null)
            onDeactivate()
          }}
        />
      )
    }
    case 'number':
      return (
        <TextField
          className="active-cell"
          type="number"
          value={value ?? ''}
          size="small"
          autoFocus
          onChange={e => {
            const nextValue = e.target.value
            onChange(nextValue === '' ? null : Number(nextValue))
          }}
          onBlur={onDeactivate}
        />
      )
    case 'percentage':
      return (
        <TextField
          className="active-cell"
          type="number"
          value={value == null ? '' : Math.round(value * 10000) / 100}
          size="small"
          autoFocus
          onChange={e => {
            const nextValue = e.target.value
            onChange(nextValue === '' ? null : Number(nextValue) / 100)
          }}
          onBlur={onDeactivate}
        />
      )
    case 'autocomplete':
      if (source === 'manufacturer') {
        return (
          <ManufacturerSearchAutocomplete
            className="active-cell"
            autoFocus
            value={value}
            contractNumber={contractNumber}
            onChange={(value, oemCode) => {
              onChange({ Manufacturer: value, OEMCode: oemCode })
            }}
            onBlur={onDeactivate}
          />
        )
      }
      return null
    default:
      if (source === 'supplier') {
        return (
          <SupplierSearchAutocomplete
            className="active-cell"
            autoFocus
            value={value}
            onChange={value => onChange(value ?? null)}
            onBlur={onDeactivate}
          />
        )
      }
      return options ? (
        <Select
          className="active-cell"
          value={String(value ?? '')}
          size="small"
          autoFocus
          onChange={e => {
            const selected = options.find(option => String(option.value ?? '') === e.target.value)
            onChange(selected ? selected.value : null)
          }}
          onBlur={onDeactivate}
        >
          {options.map((option: { label: string; value: string | number | boolean | null }) => (
            <MenuItem key={String(option.value)} value={String(option.value ?? '')}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      ) : (
        <TextField
          className="active-cell"
          value={value ?? ''}
          size="small"
          autoFocus
          onChange={e => onChange(e.target.value || null)}
          onBlur={onDeactivate}
          slotProps={{ input: { inputProps: { maxLength: limit } } }}
        />
      )
  }
}
